import { useEffect, useMemo, useRef, useState } from "react";
import { Send } from "lucide-react";
import { getCoachSession, fetchCoachHistory, streamCoach } from "@/lib/coach";

const STARTERS = ["What size tee should I get?", "Build me a 4-week 5K plan", "Find a run club near me"];

export const CoachChat = ({ compact = false }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const session = useMemo(getCoachSession, []);

  useEffect(() => {
    fetchCoachHistory(session).then(setMessages).catch(() => {});
  }, [session]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [messages]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    try {
      await streamCoach(session, msg, (delta) => {
        setMessages((m) => {
          const n = [...m];
          n[n.length - 1] = { ...n[n.length - 1], content: n[n.length - 1].content + delta };
          return n;
        });
      });
    } catch (e) {
      setMessages((m) => {
        const n = [...m];
        n[n.length - 1] = { role: "assistant", content: e.message || "Coach is unavailable — try again." };
        return n;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="coach-chat">
      <div className={`flex-1 overflow-y-auto ${compact ? "px-4 py-4" : "px-5 lg:px-0 py-6"} space-y-4`} data-testid="coach-messages">
        {messages.length === 0 && (
          <div>
            <p className="text-white/60 text-sm leading-relaxed">
              I'm NRC Coach. Ask me about training, the right tee and size, or where to find a run club near you.
            </p>
            <div className="flex flex-wrap gap-2 mt-4">
              {STARTERS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => send(s)}
                  data-testid={`coach-starter-${STARTERS.indexOf(s)}`}
                  className="font-mono-label border border-white/20 px-3 py-2 text-white/50 hover:bg-white hover:text-ink transition-colors duration-200 text-left"
                >
                  {s.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={`${i}-${m.role}`} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
            <div
              data-testid={`coach-message-${m.role}-${i}`}
              className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                m.role === "user" ? "bg-white text-ink" : "border border-white/15 text-white/80"
              }`}
            >
              {m.content || (busy && i === messages.length - 1 ? "…" : m.content)}
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
        className={`flex gap-2 border-t border-white/10 ${compact ? "p-3" : "pt-4"}`}
      >
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask about training, gear or clubs…"
          maxLength={1500}
          data-testid="coach-input"
          className="flex-1 bg-transparent border border-white/20 px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:border-white outline-none"
        />
        <button
          type="submit"
          disabled={busy || !input.trim()}
          data-testid="coach-send"
          aria-label="Send message"
          className="bg-white text-ink px-4 py-2.5 border border-white hover:bg-transparent hover:text-white transition-colors duration-200 disabled:opacity-40"
        >
          <Send size={16} />
        </button>
      </form>
    </div>
  );
};
